import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Typography from '@tiptap/extension-typography';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { common, createLowlight } from 'lowlight';

const lowlight = createLowlight(common);

interface TipTapContentProps {
  content: Record<string, unknown> | string;
  html?: string;
  className?: string;
}

export const TipTapContent: React.FC<TipTapContentProps> = ({
  content,
  html,
  className = '',
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: false,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-500 underline',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg',
        },
      }),
      Typography,
      CodeBlockLowlight.configure({
        lowlight,
        HTMLAttributes: {
          class: 'bg-muted p-4 rounded-lg font-mono text-sm overflow-x-auto',
        },
      }),
    ],
    content: typeof content === 'string' ? content : content,
    editable: false,
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg dark:prose-invert max-w-none',
      },
    },
  });

  // If HTML is provided directly, render it
  if (html && !editor) {
    return (
      <div 
        className={`prose prose-sm sm:prose lg:prose-lg dark:prose-invert max-w-none ${className}`}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }

  if (!editor) {
    return null;
  }

  return (
    <div className={className}>
      <EditorContent editor={editor} />
    </div>
  );
};

// Helper function to convert plain text to TipTap doc format
export const textToTiptapDoc = (text: string): Record<string, unknown> => {
  if (!text) {
    return { type: 'doc', content: [] };
  }

  // Split by paragraphs and create nodes
  const paragraphs = text.split(/\n\n+/).map(para => {
    // Check if it's a heading
    const headingMatch = para.match(/^(#{1,3})\s+(.+)$/);
    if (headingMatch) {
      return {
        type: 'heading',
        attrs: { level: headingMatch[1].length },
        content: [{ type: 'text', text: headingMatch[2] }],
      };
    }

    // Check if it's a code block
    if (para.startsWith('```')) {
      const codeContent = para.replace(/^```\w*\n?/, '').replace(/```$/, '');
      return {
        type: 'codeBlock',
        content: [{ type: 'text', text: codeContent }],
      };
    }

    // Check if it's a blockquote
    if (para.startsWith('>')) {
      const quoteContent = para.replace(/^>\s?/, '');
      return {
        type: 'blockquote',
        content: [
          {
            type: 'paragraph',
            content: [{ type: 'text', text: quoteContent }],
          },
        ],
      };
    }

    // Check if it's a list
    if (para.match(/^[\-\*]\s/m)) {
      const items = para.split(/\n/).filter(line => line.match(/^[\-\*]\s/));
      return {
        type: 'bulletList',
        content: items.map(item => ({
          type: 'listItem',
          content: [
            {
              type: 'paragraph',
              content: [{ type: 'text', text: item.replace(/^[\-\*]\s/, '') }],
            },
          ],
        })),
      };
    }

    // Check if it's an ordered list
    if (para.match(/^\d+\.\s/m)) {
      const items = para.split(/\n/).filter(line => line.match(/^\d+\.\s/));
      return {
        type: 'orderedList',
        content: items.map(item => ({
          type: 'listItem',
          content: [
            {
              type: 'paragraph',
              content: [{ type: 'text', text: item.replace(/^\d+\.\s/, '') }],
            },
          ],
        })),
      };
    }

    // Regular paragraph
    return {
      type: 'paragraph',
      content: [{ type: 'text', text: para }],
    };
  });

  return {
    type: 'doc',
    content: paragraphs,
  };
};

export default TipTapContent;
